import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { components } from "@/api/schema";

import { bundleKeys } from "./bundle-keys";

type Marketplace = components["parameters"]["Marketplace"];

export interface SetListingMultiplierVars {
  code: string;
  marketplace: Marketplace;
  multiplier: string;
}

/**
 * Sets a listing's own multiplier for one marketplace —
 * `PATCH /v1/listings/{code}/multiplier?marketplace=X`. Mirrors
 * useUpdateStock's shape for the SKU-row multiplier cell, and
 * useRecomputeListing's request pattern (same endpoint family). Returns
 * 204; invalidates the listings list so the refreshed multiplier and its
 * dependent sellable/allocation_sellable (recomputed server-side) come
 * back on refetch. No optimistic update — sellable/allocation_sellable
 * are backend-computed from a MIN-across-components formula the client
 * can't cheaply guess, same reasoning as useRecomputeListing.
 */
export function useSetListingMultiplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ code, marketplace, multiplier }: SetListingMultiplierVars) => {
      const { error } = await apiClient.PATCH("/v1/listings/{code}/multiplier", {
        params: { path: { code }, query: { marketplace } },
        body: { multiplier },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bundleKeys.all });
    },
  });
}
